import { sendDetailedCheckToBackend } from "./documentService";
import { generateSummaryPDF } from "../utils/pdfGenerator";
import { formatComplianceData, formatComplianceDataForPDF } from "../taskpane/formatUtils";
import { cleanTextForAnalysis } from "../utils/replaceStatements";

function cleanTextForPDF(text: string): string {
  try {
    const parsed = JSON.parse(text);
    if (parsed.compliance_results || parsed.final_compliance_summary) {
      return text;
    }
  } catch {
   
  }
  
  return cleanTextForAnalysis(text);
}

type DisplayMessageFn = (text: string, sender: "user" | "bot", variant?: "standard" | "checklist") => void;

// Helper: remove previous Detailed Check UI/message rows
function clearPreviousDetailedCheckMessages() {
  // Remove prior detailed check UI buttons/controls only; keep previous results and selection confirmations
  const selectors = ['.page-selection', '.subpart-selection', '.analysis-options'];
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      const row = (el as HTMLElement).closest('.message-row');
      if (row && row.parentNode) {
        row.parentNode.removeChild(row);
      }
    });
  });
}

export function showAnalysisCompletionOptions(displayMessage: DisplayMessageFn) {
  (window as any).displayMessage = displayMessage;
  const optionsHtml = `
    <div class="analysis-options">
      <p><strong>What would you like to do next?</strong></p>
      <button class="option-btn" onclick="handleDetailedCheck()">Detailed Check</button>
      <button class="option-btn" onclick="handleExportSummary()">Export Summary</button>
    </div>
  `;
  displayMessage(optionsHtml, "bot", "checklist");
}

export async function handleDetailedCheck(displayMessage: DisplayMessageFn) {
  (window as any).displayMessage = displayMessage;

  // Reset previous Detailed Check UI/state
  if ((window as any).resetRealtimeUiStateForDetailedCheck) {
    (window as any).resetRealtimeUiStateForDetailedCheck();
  }
  clearPreviousDetailedCheckMessages();

  try {
    const totalPages = await Word.run(async (context: any) => {
      const bodyRange = context.document.body.getRange();
      const pages = bodyRange.pages;
      pages.load();
      await context.sync();
      return pages.items.length;
    });
    let pageButtons = '';
    for (let i = 1; i <= totalPages; i++) {
      pageButtons += `<button class="option-btn" onclick="selectPageNumber(${i})">Page ${i}</button>`;
    }
    const pageOptionsHtml = `
      <div class="page-selection">
        <p><strong>Which page would you like to check?</strong></p>
        <p>Available pages: 1 to ${totalPages}</p>
        <div class="page-buttons" style="display: flex; flex-wrap: wrap; gap: 10px; width: 100%; box-sizing: border-box;">
          ${pageButtons}
        </div>
      </div>
    `;
    displayMessage(pageOptionsHtml, "bot", "checklist");
  } catch (error) {
    displayMessage("Error: Could not retrieve page information.", "bot");
  }
}

export function selectPageNumber(pageNumber: number) {
  const displayMessage = (window as any).displayMessage;
  const pageButtons = document.querySelectorAll('.page-buttons .option-btn');
  pageButtons.forEach(btn => {
    (btn as HTMLButtonElement).disabled = true;
  });

  // Remove any prior subpart selection or summaries before showing new options
  clearPreviousDetailedCheckMessages();

  const subpartOptionsHtml = `
    <div class="subpart-selection">
      <p><strong>Which subpart would you like to check for page ${pageNumber}?</strong></p>
      <p>Available subparts: A to G</p>
      <div class="subpart-buttons">
        <button class="subpart-btn" onclick="selectSubpart(${pageNumber}, 'a')">Subpart A<br><span class="description">General Labeling Provisions §§ 201.1 – 201.26 eCFR</span></button>
        <button class="subpart-btn" onclick="selectSubpart(${pageNumber}, 'b')">Subpart B<br><span class="description">Labeling Requirements for Prescription Drugs & Insulin §§ 201.50 – 201.58 eCFR</span></button>
        <button class="subpart-btn" onclick="selectSubpart(${pageNumber}, 'c')">Subpart C<br><span class="description">Labeling Requirements for Over-the-Counter (OTC) Drugs §§ 201.60 – 201.80 eCFR</span></button>
        <button class="subpart-btn" onclick="selectSubpart(${pageNumber}, 'd')">Subpart D<br><span class="description">Exemptions from Adequate Directions for Use §§ 201.100 – 201.130 eCFR</span></button>
        <button class="subpart-btn" onclick="selectSubpart(${pageNumber}, 'e')">Subpart E<br><span class="description">Other Exemptions §§ 201.150 – 201.161 eCFR</span></button>
        <button class="subpart-btn" onclick="selectSubpart(${pageNumber}, 'f')">Subpart F<br><span class="description">Labeling Claims related to Drug Efficacy Studies § 201.200 eCFR</span></button>
        <button class="subpart-btn" onclick="selectSubpart(${pageNumber}, 'g')">Subpart G<br><span class="description">Specific Labeling Requirements for Particular Drug Products §§ 201.300 – 201.328 eCFR</span></button>
      </div>
    </div>
  `;
  displayMessage(subpartOptionsHtml, "bot", "checklist");
}





export async function selectSubpart(pageNumber: number, subpart: string) {
  const displayMessage = (window as any).displayMessage;
  const subpartButtons = document.querySelectorAll('.subpart-buttons .subpart-btn');
  subpartButtons.forEach(btn => {
    (btn as HTMLButtonElement).disabled = true;
  });

  // Clear previous Detailed Check UI and show selection summary as a plain update
  clearPreviousDetailedCheckMessages();
  displayMessage(`Selected: Page ${pageNumber} and Subpart ${subpart.toUpperCase()}`, "bot");

  // Start detailed check with real-time updates
  const currentSessionId = (window as any).currentSessionId;
  if (currentSessionId) {
    // Import and start real-time updates for detailed check
    const { startRealtimeUpdates, stopRealtimeUpdates } = await import('../services/realtimeService');
    const { handleStepEvent } = (window as any);

    // Ensure previous stream is stopped and UI state is reset so new messages render
    try { stopRealtimeUpdates(); } catch {}
    if ((window as any).resetRealtimeUiStateForDetailedCheck) {
      (window as any).resetRealtimeUiStateForDetailedCheck();
    } else {
      // Fallback: minimal reset
      (window as any).isAnalysisActive = true;
    }

    // Start stream and schedule idle indicator
    if (handleStepEvent) {
      await startRealtimeUpdates(currentSessionId, handleStepEvent);
      // Brief safety delay to ensure EventSource is fully ready to receive messages
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    if ((window as any).scheduleIdleCheck) {
      (window as any).scheduleIdleCheck();
    }
  }

  try {
    const response = await sendDetailedCheckToBackend(pageNumber, subpart);
    (window as any).isAnalysisActive = false; // Stop analysis state
    if ((window as any).clearIdleTimer) (window as any).clearIdleTimer();
    if ((window as any).hideIdleIndicator) (window as any).hideIdleIndicator();

    if (response.success) {
      if (response.response) {
        displayMessage(response.response, "bot");
        // Tag the last bot message as a detailed check result so it can be removed on next Detailed Check run
        const chatBox = document.getElementById('chat-box');
        if (chatBox) {
          const rows = chatBox.querySelectorAll('.message-row.bot');
          const lastRow = rows[rows.length - 1] as HTMLElement;
          if (lastRow) lastRow.classList.add('detailed-check-result');
        }
      }
      // Show options again after detailed check summary is generated
      showAnalysisCompletionOptions(displayMessage);
    } else {
      displayMessage(`⚠ Error in detailed check: ${response.message}`, "bot");
      // Also re-show options on error to keep UX consistent
      showAnalysisCompletionOptions(displayMessage);
    }
  } catch (error) {
    displayMessage("Error: Failed to send detailed check request.", "bot");
    // Always re-show options after the attempt
    showAnalysisCompletionOptions(displayMessage);
  }
}

function wrapText(text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word.length > maxWidth ? word.substring(0, maxWidth) : word;
    }
  }
  
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines.length > 0 ? lines : [''];
}

export async function handleExportSummary(displayMessage: DisplayMessageFn) {
  (window as any).displayMessage = displayMessage;
  displayMessage("Generating export summary...", "bot");
  try {
    // Use currentSessionId (thread_id) instead of documentId for export
    const currentSessionId = (window as any).currentSessionId;
    if (!currentSessionId) {
      displayMessage("⚠ No session ID found.", "bot");
      return;
    }

    console.log(`📤 Exporting with session ID: ${currentSessionId}`);
    const response = await fetch(`http://localhost:8001/export_compliance/${currentSessionId}`);
    let detailedCheckHistory: any[] = [];
    
    if (!response.ok) {
      const storedResults = (window as any).getStoredComplianceResults();
      if (!storedResults) {
        displayMessage("⚠ No compliance summary available.", "bot");
        return;
      }
      
      const docTitle = await Word.run(async (context: any) => {
        const docProperties = context.document.properties;
        docProperties.load("title");
        await context.sync();
        return docProperties.title || "Document";
      });

      const localCommentActions = (window as any).getCommentActionsLog() || [];
      const localTableData = localCommentActions.map(item => ({
        comment: cleanTextForPDF(item.comment || 'N/A'),
        action: (item.action || 'N/A').toUpperCase(),
        reviewerComment: cleanTextForPDF(item.reviewerComment || 'N/A'),
        section: item.section || 'missing'
      }));

      const formattedResults = formatComplianceDataForPDF(storedResults);
      await generateSummaryPDF(formattedResults, docTitle, localTableData, [], undefined);
      displayMessage("📄 PDF summary downloaded successfully.", "bot");
      return;
    }

    const data = await response.json();
    detailedCheckHistory = data.detailed_check_history || [];
    console.log(`📊 Retrieved ${detailedCheckHistory.length} detailed checks from backend`);
    console.log(`📝 Retrieved comments_data:`, data.comments_data);
    console.log(`📝 Comments count:`, data.comments_data?.length || 0);
    
    if (!data.compliance_summary) {
      displayMessage("⚠ No compliance summary available.", "bot");
      return;
    }

    const docTitle = await Word.run(async (context: any) => {
      const docProperties = context.document.properties;
      docProperties.load("title");
      await context.sync();
      return docProperties.title || "Document";
    });

    // Merge backend comments with local comment actions log
    const localCommentActions = (window as any).getCommentActionsLog() || [];
    const backendComments = (data.comments_data || []).map(item => ({
      comment: cleanTextForPDF(item.comment || 'N/A'),
      action: (item.action || 'N/A').toUpperCase(),
      reviewerComment: cleanTextForPDF(item.user_input || 'N/A'),
      section: item.section || 'missing'
    }));
    
    const localComments = localCommentActions.map(item => ({
      comment: cleanTextForPDF(item.comment || 'N/A'),
      action: (item.action || 'N/A').toUpperCase(),
      reviewerComment: cleanTextForPDF(item.reviewerComment || 'N/A'),
      section: item.section || 'missing'
    }));
    
    // Combine and deduplicate based on comment text
    const allComments = [...backendComments, ...localComments];
    const uniqueComments = Array.from(
      new Map(allComments.map(item => [item.comment, item])).values()
    );
    
    console.log(`📝 Processed tableData for PDF:`, uniqueComments);
    console.log(`📝 TableData count:`, uniqueComments.length);

    const detailedCheckData = (data.detailed_check_history || []).map(item => ({
      page: item.page_number || 'N/A',
      subpart: item.subpart || 'N/A',
      score: item.overall_score || 0,
      status: item.compliance_status || 'N/A',
      satisfied: cleanTextForPDF(item.satisfied_requirements || 'N/A'),
      missing: cleanTextForPDF(item.missing_requirements || 'N/A'),
      recommendations: cleanTextForPDF(item.recommendations || 'N/A')
    }));

    const formattedResults = formatComplianceDataForPDF(JSON.stringify({
      compliance_results: data.compliance_summary
    }));
    
    const overallScore = data.overall_compliance_score || data.compliance_summary?.overall_compliance_score || 0;
    
    await generateSummaryPDF(formattedResults, docTitle, uniqueComments, detailedCheckData, overallScore);
    displayMessage("📄 PDF summary downloaded successfully.", "bot");
  } catch (error) {
    displayMessage("Error: Failed to generate export summary.", "bot");
  } finally {
    showAnalysisCompletionOptions(displayMessage);
  }
}